import React from 'react';
import {
  Container,
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Avatar,
  Paper
} from '@mui/material';
import {
  DashboardCustomize as AdminIcon,
  Search as SeoIcon,
  SmartToy as BotIcon,
  PhoneAndroid as MobileIcon,
  Speed as PerformanceIcon,
  Security as SecurityIcon,
  CheckCircle as CheckIcon,
  ArrowForward as ArrowIcon
} from '@mui/icons-material';

const Features2 = () => {
  const features = [
    {
      icon: <AdminIcon />,
      title: 'Yönetim Paneli',
      description: 'Tüm içerik türlerini tek yerden yönetin. Rol bazlı yetkilendirme ile güvenli erişim.',
      color: '#1976d2'
    },
    {
      icon: <SeoIcon />,
      title: 'SEO Optimizasyonu',
      description: 'Google uyumlu yapı ile arama motorlarında üst sıralarda yer alın.',
      color: '#2e7d32'
    },
    {
      icon: <BotIcon />,
      title: 'Otomatik Botlar',
      description: 'Haber ajanslarından otomatik içerik çekin, zaman kazanın.',
      color: '#ed6c02'
    },
    {
      icon: <MobileIcon />,
      title: 'Mobil Uyumlu',
      description: 'Tüm cihazlarda mükemmel görünüm ve kullanım deneyimi.',
      color: '#9c27b0'
    },
    {
      icon: <PerformanceIcon />,
      title: 'Yüksek Performans',
      description: 'Hızlı yükleme ve optimize edilmiş kod yapısı.',
      color: '#f44336'
    },
    {
      icon: <SecurityIcon />,
      title: 'Güvenli Altyapı',
      description: 'SSL sertifikası ve güvenlik protokolleri ile korumalı sistem.',
      color: '#00bcd4'
    }
  ];

  const stats = [
    { value: '99.9%', label: 'Uptime', color: '#2e7d32' },
    { value: '<300ms', label: 'Yükleme', color: '#ed6c02' },
    { value: '50K+', label: 'Kullanıcı', color: '#1976d2' },
    { value: '24/7', label: 'Destek', color: '#f44336' }
  ];

  const benefits = [
    'Kolay kurulum ve kullanım',
    'Teknik destek dahil',
    'Sürekli güncellemeler',
    'Özelleştirilebilir tasarım'
  ];

  return (
    <Box sx={{ py: 8, bgcolor: '#f8f9fa' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: 'text.primary'
            }}
          >
            Platform Özellikleri
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
              maxWidth: 600,
              mx: 'auto',
              fontWeight: 400
            }}
          >
            Modern teknoloji ile geliştirilmiş, kullanıcı dostu haber yönetim sistemi
          </Typography>
        </Box>

        {/* Features Grid */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  p: 3,
                  height: '100%',
                  textAlign: 'center',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: feature.color,
                    transform: 'translateY(-4px)',
                    boxShadow: 2
                  }
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: `${feature.color}15`,
                    color: feature.color,
                    width: 60,
                    height: 60,
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  {feature.icon}
                </Avatar>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {feature.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.6 }}
                >
                  {feature.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Stats */}
        <Grid container spacing={3} sx={{ mb: 8 }}>
          {stats.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 2
                }}
              >
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{ color: stat.color, mb: 1 }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Benefits */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Neden Bu Platformu Seçmelisiniz?
          </Typography>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    p: 2
                  }}
                >
                  <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                  <Typography variant="body1" fontWeight={500}>
                    {benefit}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Simple CTA */}
        <Box
          sx={{
            textAlign: 'center',
            p: 6,
            bgcolor: 'primary.main',
            color: 'white',
            borderRadius: 3
          }}
        >
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Platformumuzu Deneyin
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
            Ücretsiz demo ile tüm özellikleri keşfedin
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
          >
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowIcon />}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                fontWeight: 600,
                px: 4,
                '&:hover': {
                  bgcolor: 'grey.100'
                }
              }}
            >
              Demo Talep Et
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: 'white',
                color: 'white',
                fontWeight: 600,
                px: 4,
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              İletişime Geç
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default Features2;